/**
 * Helpers de routing des apps web (routes `/<prefix>/<proposalId>`).
 */

/**
 * Extrait le proposalId depuis un chemin de route web.
 * Les routes web ont toutes la forme `/<prefix>/<proposalId>`
 * (`/view/:id`, `/preview/:id`, `/web-editor/:id`), donc l'id est
 * le 2e segment non vide. Les slashs superflus (début/fin) sont ignorés.
 *
 * @param {string} [pathname=window.location.pathname]
 * @returns {string|null} le proposalId, ou null si absent
 */
export function getProposalIdFromPath(pathname = window.location.pathname) {
  const segments = String(pathname || '').split('/').filter(Boolean);
  return segments[1] || null;
}

/**
 * Chemin de l'éditeur web, avec l'identité du document source en paramètre.
 *
 * L'éditeur est une app autonome : elle ne connaît que le proposal. Or les
 * variables s'adressent par l'identité du document (opportunité / offre), que
 * seul l'éditeur classique possède. Elle voyage donc dans l'URL, sous les mêmes
 * noms de paramètres que le reste de l'application (`getDocumentFromParams`).
 *
 * @param {string|number} proposalId
 * @param {{ id: string|number, type: string }|null} [documentIdentity]
 */
export function webEditorPath(proposalId, documentIdentity = null) {
  const base = `/web-editor/${proposalId}`;
  const param = documentIdentityParam(documentIdentity);
  if (!param) return base;
  return `${base}?${param}=${documentIdentity.id}`;
}

/**
 * Nom du paramètre d'URL qui porte l'identité du document — `oid` pour une
 * opportunité, `ofid` pour une offre, `null` s'il n'y a rien à écrire.
 *
 * Une seule définition : l'identité doit survivre à toute navigation interne
 * de l'éditeur web (régénération comprise), sinon les variables repartent
 * sans catalogue et se figent sous leur nom.
 */
export function documentIdentityParam(documentIdentity) {
  if (documentIdentity?.id == null) return null;
  if (documentIdentity.type === 'opportunity') return 'oid';
  if (documentIdentity.type === 'offer') return 'ofid';
  return null;
}

/**
 * Chemin de la liste des documents de l'éditeur classique, `null` sans identité.
 *
 * Toujours la liste, jamais le document lui-même : un site autonome (« Site
 * web seul ») n'a pas de document classique où revenir. L'éditeur web s'ouvre
 * aussi en nouvel onglet ou depuis le back-office : l'historique du navigateur
 * n'y ramène alors pas. Sans `pid`, le classique s'ouvre sur la liste des
 * documents (cf. `DocumentsPanel`).
 */
export function documentsListPath(documentIdentity) {
  const param = documentIdentityParam(documentIdentity);
  if (!param) return null;
  return `/?${param}=${documentIdentity.id}`;
}

/** Mode revue des commentaires : le site rendu sans Puck, avec les pastilles. */
export function webReviewPath(proposalId) {
  return `/review/${proposalId}`;
}

/**
 * Identité du document lue dans la query string de l'éditeur web.
 *
 * @param {string} [search=window.location.search]
 * @returns {{ id: string, type: string }|null}
 */
export function getDocumentIdentityFromSearch(search = window.location.search) {
  const params = new URLSearchParams(search);
  const oid = params.get('oid');
  if (oid) return { id: oid, type: 'opportunity' };
  const ofid = params.get('ofid');
  if (ofid) return { id: ofid, type: 'offer' };
  return null;
}

/** Deux identités désignent-elles le même document ? */
export function sameDocumentIdentity(a, b) {
  if (!a || !b) return a == null && b == null;
  return String(a.id) === String(b.id) && a.type === b.type;
}
